import React, { FC, useMemo } from 'react';
import { formatDate } from 'utils/format';
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    Paper,
    Box,
    Typography,
} from '@mui/material';
import { Department } from 'models/organization';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { getDepartmentDaySteps, getCellCounts, getTableCellColor } from '../utils';
import { ScheduledStep } from 'models/production-planning';

type ProductionDashboardTableProps = {
    currentWeekStart: Date;
    scheduledSteps: ScheduledStep[];
    departments: Department[];
    onCellClick?: (department: string, date: Date) => void;
};

const ProductionDashboardTable: FC<ProductionDashboardTableProps> = ({
    currentWeekStart,
    scheduledSteps,
    departments: allDepartments,
    onCellClick,
}) => {
    const { t: translate } = useTranslation(['common', 'production-dashboard']);

    const days = useMemo(() => {
        return Array.from({ length: 14 }, (_, i) => {
            const d = new Date(currentWeekStart);
            d.setDate(d.getDate() + i);
            return d;
        });
    }, [currentWeekStart]);

    const departments = useMemo(() => {
        const deptSet = new Set<string>();
        scheduledSteps.forEach((s) => {
            if (s.department) deptSet.add(s.department);
        });
        const orderIndex = new Map(allDepartments.map((d, i) => [d.name, i]));
        return Array.from(deptSet).sort((a, b) => {
            const ia = orderIndex.get(a) ?? Number.MAX_SAFE_INTEGER;
            const ib = orderIndex.get(b) ?? Number.MAX_SAFE_INTEGER;
            return ia - ib;
        });
    }, [scheduledSteps, allDepartments]);

    const getCellSteps = (department: string, date: Date): ScheduledStep[] =>
        getDepartmentDaySteps(scheduledSteps, department, date);

    if (departments.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t('production-dashboard:NO_SCHEDULED_STEPS')}
            </Typography>
        );
    }

    return (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                minWidth: 140,
                            }}
                        >
                            {translate('production-dashboard:DEPARTMENT')}
                        </TableCell>
                        {days.map((day, i) => {
                            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                            return (
                                <TableCell
                                    key={i}
                                    align="center"
                                    sx={{
                                        backgroundColor: isWeekend ? '#f5f5f5' : '#fff',
                                        minWidth: 70,
                                        whiteSpace: 'nowrap',
                                        borderLeft: i === 7 ? '2px solid #ccc' : undefined,
                                    }}
                                >
                                    {formatDate(day, 'date')}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {departments.map((dept) => {
                        const deptObj = allDepartments.find((d) => d.name === dept);
                        return (
                            <TableRow key={dept}>
                                <TableCell
                                    sx={{
                                        fontWeight: 500,
                                        position: 'sticky',
                                        left: 0,
                                        backgroundColor: '#fff',
                                        zIndex: 1,
                                    }}
                                >
                                    {dept}
                                </TableCell>
                                {days.map((day, i) => {
                                    const cellSteps = getCellSteps(dept, day);
                                    const counts = getCellCounts(cellSteps, deptObj?.productionHourLimit);
                                    const bgColor = getTableCellColor(counts, deptObj?.productionHourLimit);
                                    return (
                                        <TableCell
                                            key={i}
                                            align="center"
                                            onClick={counts.orders > 0 ? () => onCellClick?.(dept, day) : undefined}
                                            sx={{
                                                backgroundColor: bgColor,
                                                cursor: counts.orders > 0 ? 'pointer' : 'default',
                                                borderLeft: i === 7 ? '2px solid #ccc' : undefined,
                                                py: 0.5,
                                                px: 0.5,
                                            }}
                                        >
                                            {counts.orders > 0 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 0.25,
                                                    }}
                                                >
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {counts.orders}
                                                    </Typography>
                                                    {counts.totalProductionHours > 0 && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                color: counts.isOverLimit
                                                                    ? '#d32f2f'
                                                                    : 'text.secondary',
                                                                fontWeight: counts.isOverLimit ? 600 : 400,
                                                            }}
                                                        >
                                                            {counts.totalProductionHours.toFixed(1)}h
                                                        </Typography>
                                                    )}
                                                    {counts.late > 0 && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    fontSize: '0.65rem',
                                                                    color: '#d32f2f',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {translate('production-dashboard:LATE_ORDERS')}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ProductionDashboardTable;
